import React, { useMemo, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { MenuGroup, Section } from '@atlaskit/menu';
import { Divider, MenuList, MenuSection, MenuSectionHeading } from '@atlaskit/navigation-system';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { InfiniteScrollView } from '../../../common/ui/infinite-scroll-view';
import { ActionsList } from '../actions-list';
import { ObjectListFlyoutEmptyState } from '../empty-state';
import { ObjectListFlyoutErrorState } from '../error-state';
import { ObjectListFlyoutLoadingState } from '../loading-state';
import { MenuItem } from '../menu-item';
import { ObjectListFlyoutNoResultsState } from '../no-results-state';
import { SearchAndFilterBar } from '../search-and-filter-bar';
import { Item, ObjectListFlyoutProps } from '../types';

export type FlyoutContentProps = Pick<
	ObjectListFlyoutProps,
	| 'label'
	| 'isSearchable'
	| 'searchPlaceholder'
	| 'searchNoResultsElem'
	| 'emptyElem'
	| 'loadingElem'
	| 'filterConfig'
	| 'useItems'
	| 'ItemComponent'
	| 'actions'
>;

export function FlyoutContent({
	label,
	isSearchable = true,
	searchPlaceholder,
	searchNoResultsElem,
	emptyElem,
	loadingElem,
	filterConfig,
	useItems,
	ItemComponent,
	actions,
}: FlyoutContentProps) {
	const [searchText, setSearchText] = useState('');
	const [selectedFilters, setSelectedFilters] = useState<string[]>(
		filterConfig?.defaultSelected ?? [],
	);

	const { sections, hasMore, loadMore, isLoading, error } = useItems(searchText, selectedFilters);
	const hasData = useMemo(
		() => !!sections?.length && sections.some(({ items }) => !!items.length),
		[sections],
	);

	const isSearchingOrFiltering = searchText || selectedFilters.length;
	// show empty state if it does not have any data & it is not searching or filtering
	const isEmptyState = !hasData && !isSearchingOrFiltering;
	const showSearchAndFilterBar = isSearchable && !isLoading && !isEmptyState;

	const headerLabel = label && (
		<Box xcss={cx(styles.heading)}>
			<Heading size="xsmall" as="h2">
				{label}
			</Heading>
		</Box>
	);

	if (error) {
		return (
			<>
				{headerLabel}
				<ObjectListFlyoutErrorState errorDescription={error?.message} />
			</>
		);
	}

	return (
		<>
			{/* Flyout menu heading */}
			{headerLabel}

			{/* Search & Filter */}
			{showSearchAndFilterBar && (
				<SearchAndFilterBar
					filterConfig={filterConfig}
					placeholder={searchPlaceholder}
					searchText={searchText}
					onSearchChange={setSearchText}
					selectedFilters={selectedFilters}
					onFilterChange={setSelectedFilters}
				/>
			)}

			<MenuGroup maxHeight={440}>
				{isLoading ? (
					(loadingElem ?? <ObjectListFlyoutLoadingState />)
				) : (
					<>
						{/* Empty states */}
						{!hasData &&
							(isSearchingOrFiltering
								? (searchNoResultsElem ?? <ObjectListFlyoutNoResultsState />)
								: (emptyElem ?? <ObjectListFlyoutEmptyState />))}

						{/* Sections & Items */}
						{hasData && (
							<Section isScrollable>
								<InfiniteScrollView
									hasMore={hasMore}
									loadMore={loadMore}
									loader={loadingElem ?? <ObjectListFlyoutLoadingState />}
								>
									{sections?.map(
										(section, index) =>
											!!section?.items?.length && (
												<MenuSection key={index}>
													<MenuSectionHeading>{section.header}</MenuSectionHeading>
													<MenuList>
														{section?.items?.map((item: Item, itemIndex: number) =>
															ItemComponent ? (
																<ItemComponent
																	key={`flyout-list-section-${index}-custom-item-${itemIndex}`}
																	{...item}
																/>
															) : (
																<MenuItem
																	key={`flyout-list-section-${index}-menu-item-${itemIndex}`}
																	{...item}
																/>
															),
														)}
													</MenuList>
												</MenuSection>
											),
									)}
								</InfiniteScrollView>
							</Section>
						)}
					</>
				)}

				{/* Actions */}
				{!!actions?.length && (
					<>
						<Divider />
						<ActionsList items={actions} />
					</>
				)}
			</MenuGroup>
		</>
	);
}

const styles = cssMap({
	heading: {
		paddingBlock: token('space.100'),
		paddingLeft: token('space.075'),
		fontWeight: token('font.weight.bold'),
		display: 'flex',
		alignItems: 'center',
	},
});
